import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterButton from 'src/components/common/FilterButton';
import DataTable from 'src/components/ui/data-table';
import { format } from 'date-fns';
import { useToast } from 'src/hooks/use-toast';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import Section from 'src/components/ui/section';
import {
  Loader2,
  CircleUser,
  Trash2,
  ClipboardCheck,
  CheckCircle2,
  ShieldAlert,
  Send,
  BriefcaseBusiness,
  Users,
  CircleCheck,
  CircleX,
  BadgeCheck,
  UserCheck,
  UserPlus,
  Clock,
  FileText,
  ClipboardPen,
  Eye,
  X,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Alert } from 'src/components/common/Alert';
import { useRequirementLocations } from 'src/services/api/queries/opportunityQueries';
import {
  useCandidatesByStatus,
  useCandidatesByStatusForTa,
} from 'src/services/api/queries/candidateQueries';
import {
  useDeleteCandidates,
  useBulkUpdateFromTASubmission,
} from 'src/services/api/mutations/candidateMutations';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import { CANDIDATE_MAPPING_STATUS_VALUES } from '../../../utils/statusConstants';
import styles from './RequirementPipeline.module.css';

// Same tonal count-badge treatment AssignmentPipeline's location dropdown
// uses for its own per-location submitted count — duplicated here (rather
// than shared) since AssignmentPipeline reads TA's `candidates_submitted`
// field while this page reads CSE's `profiles_submitted`.
const SubmittedCountBadge = ({ count }) => (
  <span className="shrink-0 text-[11px] font-semibold px-[7px] py-px rounded-[10px] leading-[1.5] bg-brand-bg-subtle text-primary">
    {count}
  </span>
);

SubmittedCountBadge.propTypes = {
  count: PropTypes.number.isRequired,
};

// Constants
const DEFAULT_PAGE_SIZE = 10;

const initialSearchResults = {
  items: [],
  total: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 1,
};

const RequirementPipeline = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive pagination from URL (Opportunities pattern)
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  const opportunityId = searchParams.get('opportunityId');
  const assignmentId = searchParams.get('assignmentId');
  const { toast } = useToast();

  // Get initial pageSize from URL or use default
  const initialPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  // State Management
  const [currentFilter, setCurrentFilter] = useState(
    searchParams.get('status') || 'submitted'
  );
  const [searchResults, setSearchResults] = useState({
    ...initialSearchResults,
    page: currentPage,
    pageSize: initialPageSize,
  });
  const [taLeadAssignmentId, setTaLeadAssignmentId] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [candidateCount, setCandidateCount] = useState({});
  // Add state for TA Progress modal and subfilter
  const [showTaProgressModal, setShowTaProgressModal] = useState(false);
  const [taProgressSubFilter, setTaProgressSubFilter] = useState('pending');
  const [taProgressSearchResults, setTaProgressSearchResults] = useState({
    ...initialSearchResults,
  });
  const [taProgressCandidateCount, setTaProgressCandidateCount] = useState({});
  const [requirementLocationId, setRequirementLocationId] = useState(null);

  useEffect(() => {
    if (showTaProgressModal) {
      // Modal opened
    }
  }, [showTaProgressModal, taLeadAssignmentId, requirementLocationId]);

  const {
    data: requirementLocationsResponse,
    isLoading: isRequirementLocationsLoading,
    error: requirementLocationsError,
  } = useRequirementLocations(opportunityId, {
    enabled: !!opportunityId,
    assignmentId,
  });
  const requirementLocations =
    requirementLocationsResponse?.data?.requirement_locations || [];
  const opportunityData =
    requirementLocationsResponse?.data?.requirement_info || [];

  useEffect(() => {
    const taAssignmentId =
      requirementLocationsResponse?.data?.requirement_info?.ta_assignment_id;
    if (taAssignmentId && taAssignmentId !== taLeadAssignmentId) {
      setTaLeadAssignmentId(taAssignmentId);
    }
  }, [requirementLocationsResponse, taLeadAssignmentId]);
  useEffect(() => {
    if (!requirementLocationId && requirementLocations.length > 0) {
      const firstId = requirementLocations[0]?.requirement_location_id;
      if (firstId) {
        setRequirementLocationId(firstId.toString());
      }
    }
  }, [requirementLocationId, requirementLocations]);
  // Move taFilters definition above isStatusFromTA
  const taFilters = [
    {
      label: 'New',
      value: 'new',
      statsLabel: 'new_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: <FileText className="h-4 w-4" />,
    },
    {
      label: 'Test Link Send',
      value: 'pending',
      statsLabel: 'pending_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: <Send className="h-4 w-4" />,
    },
    {
      label: 'Test in progress',
      value: 'in_progress',
      statsLabel: 'in_progress_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: <ClipboardPen className="h-4 w-4" />,
    },
    {
      label: 'Passed',
      value: 'passed',
      statsLabel: 'passed_count',
      activeClass: 'bg-green-100 text-green-800 border border-green-300',
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      label: 'Failed',
      value: 'failed',
      statsLabel: 'failed_count',
      activeClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: <CircleX className="h-4 w-4" />,
    },
  ];
  const cseTaFilters = [
    {
      label: 'Pending Test Link ',
      value: 'new',
      statsLabel: 'new_count',
      icon: <Clock className="h-4 w-4" />,
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Test Link Send',
      value: 'pending',
      statsLabel: 'pending_count',
      icon: <CircleCheck className="h-4 w-4" />,
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Test in progress',
      value: 'in_progress',
      statsLabel: 'in_progress_count',
      icon: <Clock className="h-4 w-4" />,
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Passed',
      value: 'passed',
      statsLabel: 'passed_count',
      icon: <CheckCircle2 className="h-4 w-4" />,
      activeClass: 'bg-green-100 text-green-800 border border-green-300',
    },
    {
      label: 'Failed',
      value: 'failed',
      statsLabel: 'failed_count',
      icon: <CircleX className="h-4 w-4" />,
      activeClass: 'bg-red-100 text-red-800 border border-red-300',
    },
  ];

  // Determine which query to use based on user role and filter
  const isStatusFromTA = taFilters.some(
    (filter) => filter.value === currentFilter
  );
  // CSE views the CSE filter bar by default, but switches to the TA-status
  // query when they've drilled into a TA sub-filter (e.g. "Test Link Send").
  const shouldUseTaQuery = isStatusFromTA;

  // React Query for candidates data - always call both hooks but enable conditionally
  const {
    data: taCandidatesData,
    isLoading: isTaCandidatesLoading,
    error: taCandidatesError,
    refetch: refetchTaCandidates,
  } = useCandidatesByStatusForTa({
    opportunityId,
    taLeadAssignmentId,
    status: currentFilter,
    page: searchResults.page,
    pageSize: searchResults.pageSize,
    requirementLocationId,
    enabled:
      shouldUseTaQuery && !!taLeadAssignmentId && !!requirementLocationId,
  });

  const {
    data: cseCandidatesData,
    isLoading: isCseCandidatesLoading,
    error: cseCandidatesError,
    refetch: refetchCseCandidates,
  } = useCandidatesByStatus({
    opportunityId,
    status: currentFilter,
    page: searchResults.page,
    pageSize: searchResults.pageSize,
    requirementLocationId,
    enabled: !shouldUseTaQuery && !!opportunityId && !!requirementLocationId,
  });

  // Select the appropriate data based on which query should be active
  let candidatesData;
  let isCandidatesLoading;
  let candidatesError;
  let refetchCandidates;

  if (shouldUseTaQuery) {
    candidatesData = taCandidatesData;
    isCandidatesLoading = isTaCandidatesLoading;
    candidatesError = taCandidatesError;
    refetchCandidates = refetchTaCandidates;
  } else {
    candidatesData = cseCandidatesData;
    isCandidatesLoading = isCseCandidatesLoading;
    candidatesError = cseCandidatesError;
    refetchCandidates = refetchCseCandidates;
  }

  const finalCandidatesData = candidatesData;
  const finalIsLoading = isCandidatesLoading;
  const finalError = candidatesError;
  // React Query mutation for deleting candidates
  const deleteCandidatesMutation = useDeleteCandidates();
  const bulkUpdateFromTASubmissionMutation = useBulkUpdateFromTASubmission();

  const routingStatus = opportunityData?.ta_routing_status;

  // Define filters after routingStatus is available
  const cseFilters = [
    ...(routingStatus === 'ASSIGNED'
      ? [
          {
            label: 'TA Submission',
            value: 'screened',
            statsLabel: 'screened_count',
            icon: <UserPlus className="h-4 w-4" />,
            activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
          },
        ]
      : []),
    {
      label: 'Submitted',
      value: 'submitted',
      statsLabel: 'submitted_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: <Users className="h-4 w-4" />,
    },
    {
      label: 'Shortlisted',
      value: 'shortlisted',
      statsLabel: 'shortlisted_count',
      icon: <UserCheck className="h-4 w-4" />,
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Interview',
      value: 'pending_interview',
      statsLabel: 'pending_interview_count',
      icon: <ClipboardPen className="h-4 w-4" />,
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Rejected',
      value: 'rejected',
      statsLabel: 'rejected_count',
      activeClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: <CircleX className="h-4 w-4" />,
    },
    {
      label: 'Offers Made',
      value: 'offer_extended',
      statsLabel: 'offer_extended_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: <Send className="h-4 w-4" />,
    },
    {
      label: 'Offers Accepted',
      value: 'offer_accepted',
      activeClass: 'bg-green-100 text-green-800 border border-green-300',
      statsLabel: 'offer_accepted_count',
      icon: <BadgeCheck className="h-4 w-4" />,
    },
    {
      label: 'Joined',
      value: 'joined',
      statsLabel: 'joined_count',
      icon: <CircleUser className="h-4 w-4" />,
      activeClass: 'bg-green-100 text-green-800 border border-green-300',
    },
  ];

  const oppDetailParams = new URLSearchParams();
  const rlIdForBreadcrumb =
    searchParams.get('requirementLocationId') || requirementLocationId;
  if (rlIdForBreadcrumb)
    oppDetailParams.set('requirementLocationId', rlIdForBreadcrumb);
  if (assignmentId) oppDetailParams.set('assignmentId', assignmentId);
  const oppDetailHref = opportunityId
    ? `/opportunity/${opportunityId}${oppDetailParams.toString() ? `?${oppDetailParams.toString()}` : ''}`
    : null;

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    ...(oppDetailHref
      ? [
          {
            label: opportunityData?.title || 'Opportunity',
            href: oppDetailHref,
          },
        ]
      : []),
    { label: 'Candidates' },
  ];

  const isViewingTaStatus =
    routingStatus === 'ASSIGNED' &&
    taFilters.some((f) => f.value === currentFilter);

  // Process candidates data when it changes
  useEffect(() => {
    const dataToProcess = finalCandidatesData;
    if (dataToProcess?.success) {
      // Handle both response formats: external candidates use pagination.total, regular use total_candidates
      const total =
        dataToProcess.data.total_candidates ??
        dataToProcess.data.pagination?.total ??
        0;
      setSearchResults({
        items: dataToProcess.data.items || [],
        total,
        page: dataToProcess.data.pagination?.page ?? 1,
        pageSize:
          dataToProcess.data.pagination?.page_size ?? searchResults.pageSize,
        totalPages: dataToProcess.data.pagination?.total_pages ?? 1,
      });
      // Stats might not be available for external candidates
      if (dataToProcess.data.stats) {
        const { stats } = dataToProcess.data;
        const selectedStats =
          requirementLocationId && stats?.[requirementLocationId.toString()]
            ? stats[requirementLocationId.toString()]
            : stats;
        setCandidateCount(selectedStats);
      }
    }
  }, [finalCandidatesData, requirementLocationId]);

  // Handle candidates error
  useEffect(() => {
    if (finalError) {
      toast({
        message: finalError.message || 'Failed to fetch candidates',
        variant: 'destructive',
      });
    }
  }, [finalError, toast]);

  // URL Params Management
  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.status) updatedParams.set('status', params.status);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  // CRITICAL: Add this missing useEffect for URL status handling
  useEffect(() => {
    const status = searchParams.get('status');
    if (status) {
      setCurrentFilter(status);
      if (taFilters.some((f) => f.value === status)) {
        // Set From TA as active parent filter when TA status is in URL
        setCurrentFilter(status);
      }
    }
  }, [searchParams.get('status')]);

  useEffect(() => {
    if (requirementLocationsError) {
      toast({
        message:
          requirementLocationsError.message ||
          'Failed to fetch opportunity details',
        variant: 'destructive',
      });
    }
  }, [requirementLocationsError, toast]);

  useEffect(() => {
    if (
      opportunityData &&
      routingStatus !== 'ASSIGNED' &&
      taFilters.some((f) => f.value === currentFilter)
    ) {
      setCurrentFilter('new');
      updateUrlParams({ status: 'new', page: 1 });
    }
  }, [opportunityData, routingStatus]);

  // Event Handlers
  const handleRequirementLocationChange = (selectedId) => {
    setRequirementLocationId(selectedId);
    setSelectedCandidates([]);
    updateUrlParams({ page: 1 });
  };

  const selectedLocation = requirementLocations.find(
    (location) =>
      location.requirement_location_id.toString() === requirementLocationId
  );

  // Mirrors AssignmentPipeline.jsx's own header location dropdown (name +
  // employment type, with a submitted-count badge per option) — moved up
  // here from the requirement-summary card so it sits in the page header
  // alongside "View Matches", same placement as the TA-side pipeline.
  const locationSelect = requirementLocations.length > 0 && (
    <Select
      value={requirementLocationId || undefined}
      onValueChange={handleRequirementLocationChange}
    >
      <SelectTrigger className="w-64 h-9 text-sm rounded-lg bg-card text-foreground-strong border-input">
        <SelectValue placeholder="Select location">
          {selectedLocation && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="truncate">
                {selectedLocation.location_name} —{' '}
                {selectedLocation.employment_type}
              </span>
              {selectedLocation.profiles_submitted > 0 && (
                <SubmittedCountBadge
                  count={selectedLocation.profiles_submitted}
                />
              )}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {requirementLocations.map((location) => (
          <SelectItem
            key={location.requirement_location_id}
            value={location.requirement_location_id.toString()}
          >
            <span className="inline-flex items-center gap-1.5">
              <span>
                {location.location_name} — {location.employment_type}
              </span>
              {location.profiles_submitted > 0 && (
                <SubmittedCountBadge count={location.profiles_submitted} />
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setSelectedCandidates([]);
    updateUrlParams({ status: filter, page: 1 });
  };

  const handleSubFilterChange = (filter) => {
    setCurrentFilter(filter);
    setSelectedCandidates([]);
    updateUrlParams({ status: filter, page: 1 });
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: newPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    const size = parseInt(newPageSize, 10);
    updateUrlParams({ pageSize: size, page: 1 });
  };

  // CRITICAL: Add this missing useEffect for searchParams changes
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus && urlStatus !== currentFilter) {
      setCurrentFilter(urlStatus);
    }
  }, [searchParams]);

  // Effects
  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, [opportunityId]);

  // Refetch candidates when landing on this screen
  useEffect(() => {
    if (opportunityId && currentFilter) {
      refetchCandidates();
    }
  }, [
    opportunityId,
    currentFilter,
    searchResults.page,
    searchResults.pageSize,
    requirementLocationId,
    refetchCandidates,
  ]);

  const handleSelectAll = (checked) => {
    if (checked) {
      const allCandidateIds = candidatesData?.data?.items?.map(
        (candidate) => candidate.candidate_id
      );
      setSelectedCandidates(allCandidateIds || []);
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleSelectCandidate = (e, candidateId) => {
    e.stopPropagation(); // Prevent row click when clicking checkbox
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const initiateDelete = () => {
    if (selectedCandidates.length === 0) {
      toast({
        message: 'Please select at least one candidate',
        variant: 'warning',
      });
      return;
    }
    setShowDeleteAlert(true);
  };

  const handleDeleteCandidates = async () => {
    try {
      const data = {
        candidates: selectedCandidates,
        requirement_location_id: requirementLocationId
          ? parseInt(requirementLocationId, 10)
          : null,
      };

      await deleteCandidatesMutation.mutateAsync({
        id: opportunityId,
        data,
      });

      toast({
        message: 'Candidates deleted successfully',
        variant: 'success',
      });
      setSelectedCandidates([]);
      setShowDeleteAlert(false);
    } catch (error) {
      toast({
        message: error.message || 'Failed to delete candidates',
        variant: 'destructive',
      });
    }
  };

  const handleBulkSubmitFromTASubmission = async () => {
    try {
      if (selectedCandidates.length === 0) {
        toast({
          title: 'Warning',
          description: 'Please select at least one candidate',
          variant: 'warning',
        });
        return;
      }

      // Get mapping data for selected candidates
      const candidates = finalCandidatesData?.data?.items || [];
      const selectedCandidateData = candidates.filter((candidate) =>
        selectedCandidates.includes(candidate.candidate_id)
      );

      // Build the payload with candidate_id and requirement_location_id
      const candidatesPayload = selectedCandidateData.map((candidate) => ({
        candidate_id: candidate.candidate_id,
        requirement_location_id: parseInt(requirementLocationId, 10),
      }));

      if (candidatesPayload.length === 0) {
        throw new Error('No valid candidates found for submission');
      }

      const data = {
        status: 'SUBMITTED',
        candidates: candidatesPayload,
      };

      await bulkUpdateFromTASubmissionMutation.mutateAsync({
        opportunityId,
        data,
      });

      toast({
        message: `${candidatesPayload.length} candidate(s) submitted successfully`,
        variant: 'success',
      });
      setSelectedCandidates([]);
    } catch (error) {
      toast({
        message: error.message || 'Failed to submit candidates',
        variant: 'destructive',
      });
    }
  };

  const navigateToProfile = (candidateId) => {
    navigate(
      `/candidates/${candidateId}?status=${currentFilter}&opportunityId=${opportunityId}${
        assignmentId ? `&assignmentId=${assignmentId}` : ''
      }${requirementLocationId ? `&requirementLocationId=${requirementLocationId}` : ''}`
    );
  };

  const handleVerifyCandidateClick = (e, candidateId) => {
    e.stopPropagation(); // Prevent row click event
    const p = new URLSearchParams({ status: currentFilter });
    if (opportunityId) p.set('opportunityId', opportunityId);
    if (assignmentId) p.set('assignmentId', assignmentId);
    if (requirementLocationId)
      p.set('requirementLocationId', requirementLocationId);
    navigate(`/candidates/${candidateId}?${p.toString()}`);
  };

  const handleShowScores = (candidate) => {
    // Navigate to update score form with candidate information
    // Add readonly flag for passed and failed candidates
    const isReadonly = ['passed', 'failed'].includes(candidate.status);
    // Handle external candidates with candidate_name vs regular candidates with first_name/last_name
    const firstName =
      candidate.first_name ||
      (candidate.candidate_name ? candidate.candidate_name.split(' ')[0] : '');
    const lastName =
      candidate.last_name ||
      (candidate.candidate_name
        ? candidate.candidate_name.split(' ').slice(1).join(' ')
        : '');
    const scoreFormUrl = `/candidates/update-score?candidateId=${candidate.candidate_id}&opportunityId=${opportunityId}&status=${currentFilter}${
      assignmentId ? `&assignmentId=${assignmentId}` : ''
    }${requirementLocationId ? `&requirementLocationId=${requirementLocationId}` : ''}&firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}&email=${encodeURIComponent(candidate.email || '')}&phone=${encodeURIComponent(candidate.phone || '')}${isReadonly ? '&readonly=true' : ''}`;
    navigate(scoreFormUrl);
  };

  // Format number as currency with commas
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (Number.isNaN(numAmount)) return '0';
    return numAmount.toLocaleString('en-IN');
  };

  // Render Methods
  const parseDateSafely = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

    // Handle numeric timestamps
    if (typeof value === 'number') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const str = String(value).trim();
    if (!str) return null;

    // Try native Date parsing first (handles ISO strings)
    let d = new Date(str);
    if (!Number.isNaN(d.getTime())) return d;

    // Handle common dd/mm/yyyy or dd-mm-yyyy formats
    const m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (m) {
      const day = Number(m[1]);
      const month = Number(m[2]);
      const year = Number(m[3].length === 2 ? `20${m[3]}` : m[3]);
      d = new Date(year, month - 1, day);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    return null;
  };

  const formatDate = (value) => {
    const d = parseDateSafely(value);
    return d ? format(d, 'dd MMM yyyy') : '-';
  };
  const renderFilters = () => (
    <div className="space-y-2 border border-gray-200 rounded-t-md px-4 bg-white border-b-none">
      <FilterButton
        filters={cseFilters}
        defaultActiveFilter={currentFilter}
        onFilterChange={handleFilterChange}
        countStats={candidateCount}
      />
      {isViewingTaStatus && routingStatus === 'ASSIGNED' && (
        <FilterButton
          filters={taFilters}
          defaultActiveFilter={currentFilter}
          onFilterChange={handleSubFilterChange}
          isSubFilter
          countStats={candidateCount}
        />
      )}
    </div>
  );

  // Columns for DataTableV2 (main list)
  const candidates = finalCandidatesData?.data?.items || [];
  const showHeaderCheckbox = ['new', 'submitted', 'screened'].includes(
    currentFilter
  );
  const showRowCheckbox = ['new', 'submitted', 'screened'].includes(
    currentFilter
  );
  const allSelected =
    candidates.length > 0 &&
    candidates.every((c) => selectedCandidates.includes(c.candidate_id));

  const selectHeaderNode = showHeaderCheckbox ? (
    <div className="flex items-center ml-2 w-full h-full">
      <Checkbox
        checked={allSelected}
        onCheckedChange={(val) => handleSelectAll(Boolean(val))}
      />
    </div>
  ) : null;

  const mainColumns = [];

  mainColumns.push({
    key: 'select',
    header: selectHeaderNode,
    className: 'w-14 text-center relative overflow-hidden',
    align: 'center',
    render: (_value, row) =>
      showRowCheckbox ? (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          data-prevent-row-click="true"
          role="button"
          tabIndex={0}
          aria-label="Select candidate"
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
        >
          <Checkbox
            checked={selectedCandidates.includes(row.candidate_id)}
            onCheckedChange={() =>
              handleSelectCandidate(
                { stopPropagation: () => {} },
                row.candidate_id
              )
            }
          />
        </div>
      ) : null,
  });

  mainColumns.push({
    key: 'name',
    header: 'Name',
    className: 'text-sm',
    tooltip: true,
    render: (_value, row) => {
      // Handle external candidates with candidate_name field
      if (row.candidate_name) {
        return row.candidate_name;
      }
      // Handle regular candidates with first_name and last_name
      return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-';
    },
  });
  mainColumns.push({
    key: 'email',
    header: 'Email',
    className: 'text-sm',
    tooltip: true,
    render: (_value, row) => row.email || '-',
  });
  mainColumns.push({
    key: 'phone',
    header: 'Phone',
    className: 'text-sm',
    render: (_value, row) => row.phone || '-',
  });

  if (currentFilter === 'joined') {
    mainColumns.push({
      key: 'ctc_amount',
      header: 'CTC',
      className: 'text-sm',
      render: (_v, row) => `₹${formatCurrency(row.ctc_amount)}`,
    });
    mainColumns.push({
      key: 'joining_date',
      header: 'Joining Date',
      className: 'text-sm',
      render: (_v, row) =>
        row.joining_date ? formatDate(row.joining_date) : '-',
    });
  } else {
    mainColumns.push({
      key: 'status',
      header: 'Status',
      className: 'text-sm',
      render: (_v, row) => CANDIDATE_MAPPING_STATUS_VALUES[row.status],
    });
  }

  mainColumns.push({
    key: 'mapped_at',
    header: 'Updated on',
    className: 'text-sm',
    render: (_v, row) => formatDate(row.mapped_at),
  });

  if (currentFilter === 'joined') {
    mainColumns.push({
      key: 'verification',
      header: 'Verification',
      className: 'text-sm',
      render: (_v, row) => (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div>
                {row.joining_date_verified ? (
                  <Button type="button" variant="ghost" size="sm">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) =>
                      handleVerifyCandidateClick(e, row.candidate_id)
                    }
                  >
                    <ShieldAlert className="h-5 w-5 text-amber-600" />
                  </Button>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="p-4 bg-blue-50 text-primary text-xs rounded w-48">
              <div className="space-y-1">
                <p>
                  <span className="font-semibold">Joining Date:</span>{' '}
                  {row.joining_date ? formatDate(row.joining_date) : '-'}
                </p>
                <p>
                  <span className="font-semibold">CTC:</span> ₹
                  {formatCurrency(row.ctc_amount)}
                </p>
                <p className="text-xs text-primary/70 mt-1">
                  {row.joining_date_verified
                    ? 'Details have been verified'
                    : 'Details pending verification'}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    });
  }

  mainColumns.push({
    key: 'view_scores',
    header: '',
    className: 'text-sm text-center',
    render: (_v, row) =>
      ['in_progress', 'passed', 'in-progress', 'failed'].includes(
        row.status
      ) ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleShowScores(row);
          }}
        >
          <ClipboardCheck className="h-5 w-5 text-primary" />
        </Button>
      ) : null,
  });

  // Columns for TA Progress modal
  const modalColumns = [
    {
      key: 'name',
      header: 'Name',
      className: 'text-sm',
      tooltip: true,
      render: (_v, row) => {
        // Handle external candidates with candidate_name field
        if (row.candidate_name) {
          return row.candidate_name;
        }
        // Handle regular candidates with first_name and last_name
        return `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-';
      },
    },
    {
      key: 'email',
      header: 'Email',
      className: 'text-sm',
      tooltip: true,
      render: (_value, row) => row.email || '-',
    },
    {
      key: 'phone',
      header: 'Phone',
      className: 'text-sm',
      render: (_value, row) => row.phone || '-',
    },
    {
      key: 'mapped_at',
      header: 'Updated on',
      className: 'text-sm',
      render: (_v, row) => formatDate(row.mapped_at),
    },
    {
      key: 'view_scores',
      header: '',
      className: 'text-sm text-center',
      render: (_v, row) =>
        ['in_progress', 'passed', 'in-progress', 'failed'].includes(
          row.status
        ) ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation();
              handleShowScores(row);
            }}
          >
            <ClipboardCheck className="h-4 w-4 text-primary" />
          </Button>
        ) : null,
    },
  ];

  // Fetch TA Progress candidates (pending + subfilters) for modal
  const {
    data: taProgressCandidatesData,
    isLoading: isTaProgressCandidatesLoading,
  } = useCandidatesByStatusForTa({
    taLeadAssignmentId,
    requirementLocationId,
    status: taProgressSubFilter,
    page: taProgressSearchResults.page,
    pageSize: taProgressSearchResults.pageSize,
    enabled: showTaProgressModal,
  });

  useEffect(() => {
    // Effect for taProgressCandidatesData and requirementLocationId
  }, [taProgressCandidatesData, requirementLocationId]);
  // Update modal table data when query changes
  useEffect(() => {
    if (taProgressCandidatesData?.success) {
      setTaProgressSearchResults({
        items: taProgressCandidatesData.data.items,
        total: taProgressCandidatesData.data.total_candidates,
        page: taProgressCandidatesData.data.pagination.page,
        pageSize: taProgressCandidatesData.data.pagination.page_size,
        totalPages: taProgressCandidatesData.data.pagination.total_pages,
      });
      if (taProgressCandidatesData.data.stats) {
        setTaProgressCandidateCount(taProgressCandidatesData.data.stats);
      }
    }
  }, [taProgressCandidatesData]);

  // Modal subfilter change
  const handleTaProgressSubFilterChange = (filter) => {
    setTaProgressSubFilter(filter);
    setTaProgressSearchResults((prev) => ({ ...prev, page: 1 }));
  };

  const handleTaProgressPageChange = (newPage) => {
    setTaProgressSearchResults((prev) => ({ ...prev, page: newPage }));
  };
  const handleTaProgressPageSizeChange = (newPageSize) => {
    const size = parseInt(newPageSize, 10);
    setTaProgressSearchResults((prev) => ({
      ...prev,
      pageSize: size,
      page: 1,
    }));
  };

  return (
    <div className="text-left bg-muted min-h-screen">
      <PageHeader
        title="Candidates"
        breadcrumbs={breadcrumbItems}
        actions={
          <div className="flex items-center gap-2">
            {locationSelect}
            {/* {currentFilter === 'screened' && routingStatus === 'ASSIGNED' && (
              <Button
                variant="outline"
                size="headerSm"
                onClick={() => setShowTaProgressModal(true)}
              >
                TA Assignment progress
              </Button>
            )} */}
            {/* This page is CSE-only (see CLAUDE.md — TA got its own
                AssignmentPipeline view once TA Lead was removed), so no
                role check is needed here: a CSE only ever browses matches
                on the destination page (/candidates/submit), never submits
                from it — that page's own selection/submit UI is hidden for
                isCse. "View Matches" + Eye reflects that read-only purpose,
                rather than implying a submit action CSE can't take. */}
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => {
                const submitUrl = `/candidates/submit?opportunityId=${opportunityId}${assignmentId ? `&assignmentId=${assignmentId}` : ''}${requirementLocationId ? `&requirementLocationId=${requirementLocationId}` : ''}`;
                navigate(submitUrl);
              }}
            >
              <Eye className="w-3.5 h-3.5" strokeWidth={1.8} />
              View Matches
            </Button>
          </div>
        }
      />
      <PageContainer className="p-4 space-y-4">
        {isRequirementLocationsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          opportunityData && (
            <Section className="!mt-4 !rounded-xl !border-border !shadow-[0_1px_3px_rgba(0,0,0,.04)]">
              <div className="flex items-start gap-2">
                <span className="flex items-center justify-center bg-brand-bg-subtle rounded-[10px] p-2 w-10 h-10 flex-shrink-0">
                  <BriefcaseBusiness
                    className="h-5 w-5 text-primary"
                    strokeWidth={1.8}
                  />
                </span>
                <div className="flex flex-col justify-between items-start gap-1 w-full">
                  <h3 className="text-[14px] font-semibold text-foreground-strong tracking-[-0.01em] flex items-center gap-2">
                    {opportunityData?.title}
                  </h3>
                  <p className="text-[13px] text-muted-foreground">
                    All candidates submitted for this requirement are listed
                    below. Use the status tabs to filter.
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {opportunityData?.organization && (
                      <span className="text-[12px] text-muted-foreground border border-border rounded-md px-2 py-1">
                        {opportunityData.organization}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Section>
          )
        )}
        <div>
          <div>{renderFilters()}</div>
          <DataTable
            className="border-border rounded-t-none"
            headerClassName="text-[10.5px]"
            data={candidates}
            columns={mainColumns}
            rowKey="candidate_id"
            hover
            stickyHeader
            loading={finalIsLoading}
            onRowClick={(row) => navigateToProfile(row.candidate_id)}
            pagination
            currentPage={currentPage}
            totalPages={searchResults.totalPages}
            pageSize={currentPageSize}
            totalItems={searchResults.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyState={
              <div className="flex flex-col items-center justify-center py-[60px] px-5 text-center bg-card rounded-b-md border border-t-0 border-border">
                <div className="w-[72px] h-[72px] rounded-full bg-border flex items-center justify-center mb-5">
                  <Users
                    className="w-8 h-8 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
                  No candidates found
                </p>
                <p className="text-[13px] text-muted-foreground leading-[1.6] max-w-[320px]">
                  No candidates match the current filter.
                </p>
              </div>
            }
          />
          {/* Floating bulk-action bar, matching AssignmentPipeline's
              ManualActionsBar placement/behavior — fixed to the bottom of
              the viewport, slides in via .bulkOn once a row is selected,
              rather than the old inline buttons in the requirement summary
              card above. Only mounted on tabs that actually have a bulk
              action (submitted/screened) — 'new' shows row checkboxes but
              has never had a bulk action here, so nothing changes for it. */}
          {['submitted', 'screened'].includes(currentFilter) && (
            <div
              className={cn(
                styles.bulk,
                selectedCandidates.length > 0 && styles.bulkOn
              )}
            >
              <span className={styles.cnt}>
                <b className={styles.cntNum}>{selectedCandidates.length}</b>{' '}
                selected
              </span>
              <span className={styles.vr} />
              <div className={styles.actions}>
                {currentFilter === 'screened' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkSubmitFromTASubmission}
                    disabled={bulkUpdateFromTASubmissionMutation.isPending}
                    className="flex items-center gap-1 text-xs"
                  >
                    {bulkUpdateFromTASubmissionMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5" />
                        Submit Selected
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={initiateDelete}
                  disabled={deleteCandidatesMutation.isPending}
                  className="flex items-center gap-1 text-xs"
                >
                  {deleteCandidatesMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete Selected
                    </>
                  )}
                </Button>
              </div>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setSelectedCandidates([])}
                aria-label="Clear selection"
              >
                <X />
              </button>
            </div>
          )}
          <Alert
            isOpen={showDeleteAlert}
            onClose={() => setShowDeleteAlert(false)}
            title="Confirm Delete"
            description={`Are you sure you want to delete ${
              selectedCandidates.length
            } selected candidate${selectedCandidates.length > 1 ? 's' : ''}?`}
            cancelText="Cancel"
            continueText="Delete"
            onCancel={() => setShowDeleteAlert(false)}
            onContinue={handleDeleteCandidates}
          />
        </div>
        <Dialog
          open={showTaProgressModal}
          onOpenChange={setShowTaProgressModal}
        >
          <DialogContent className="max-w-none w-[80vw] h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>TA Secondary Assignments</DialogTitle>
            </DialogHeader>
            <div className="mb-4">
              <FilterButton
                filters={cseTaFilters}
                defaultActiveFilter={taProgressSubFilter}
                onFilterChange={handleTaProgressSubFilterChange}
                countStats={taProgressCandidateCount}
              />
            </div>
            <DataTable
              data={taProgressCandidatesData?.data?.items || []}
              columns={modalColumns}
              rowKey="candidate_id"
              hover
              stickyHeader
              loading={isTaProgressCandidatesLoading}
              emptyState={
                <div className="flex flex-col items-center justify-center py-[60px] px-5 text-center">
                  <div className="w-[72px] h-[72px] rounded-full bg-border flex items-center justify-center mb-5">
                    <Users
                      className="w-8 h-8 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
                    No candidates found
                  </p>
                  <p className="text-[13px] text-muted-foreground leading-[1.6] max-w-[320px]">
                    No candidates match the current filter.
                  </p>
                </div>
              }
              onRowClick={(row) => navigateToProfile(row.candidate_id)}
              pagination
              currentPage={taProgressSearchResults.page}
              totalPages={taProgressSearchResults.totalPages}
              pageSize={taProgressSearchResults.pageSize}
              totalItems={taProgressSearchResults.total}
              onPageChange={handleTaProgressPageChange}
              onPageSizeChange={handleTaProgressPageSizeChange}
              className="border-border"
            />
          </DialogContent>
        </Dialog>
      </PageContainer>
    </div>
  );
};

export default RequirementPipeline;
